import {
  AfterViewInit,
  Component,
  ElementRef,
  EventEmitter,
  HostBinding,
  HostListener,
  Input,
  Output,
  ViewChild,
} from '@angular/core'
import {
  Attachment,
  UploadBoxComponent,
  UploadBoxOptions,
  addAttachmentsComplete,
  defaultUploadBoxOptions,
} from '@tokeet-frontend/tv3-platform'
import { AuthService } from '@tv3/services/auth.service'
import { Store } from '@ngrx/store'
import * as R from 'ramda'

@Component({
  selector: 'app-drag-drop-upload-overlay',
  templateUrl: './drag-drop-upload-overlay.component.html',
  styleUrls: ['./drag-drop-upload-overlay.component.scss'],
})
export class DragDropUploadOverlayComponent implements AfterViewInit {
  @Input() options: UploadBoxOptions = {}
  @Input() enabled = true
  @Input() showFileList = false
  @Input() showUploadBox = true
  @Input() overlayText: string = $localize`Drop files to attach`
  @Input() dispatchToStore = true
  @Input() allowDocumentDrop = false

  @Output() uploaded = new EventEmitter<Attachment[]>()
  @Output() dragActiveChange = new EventEmitter<boolean>()

  @ViewChild('uploadBox') uploadBox: UploadBoxComponent

  @HostBinding('class.active')
  get isActive() {
    return this.isDragging && this.enabled
  }

  isDragging = false
  isUploading = false

  constructor(private elRef: ElementRef<HTMLElement>, private authService: AuthService, private store: Store) {}

  ngAfterViewInit(): void {}

  @HostListener('window:resize')
  onResize() {
    this.refreshBounds()
  }

  @HostListener('document:dragover', ['$event'])
  onDocumentDragOver(event: DragEvent) {
    const hasFiles = this.hasFiles(event)
    const inside = this.isInside(event)
    if (!this.enabled || !hasFiles) {
      return
    }
    if (!inside && !this.allowDocumentDrop) {
      return
    }
    event.preventDefault()
    event.stopPropagation()
    this.setDragging(true)
  }

  @HostListener('document:dragleave', ['$event'])
  onDocumentDragLeave(event: DragEvent) {
    if (this.isUploading || !this.isDragging || !this.hasFiles(event) || this.isInside(event)) {
      return
    }
    this.setDragging(false)
  }

  @HostListener('document:drop', ['$event'])
  onDocumentDrop(event: DragEvent) {
    const hasFiles = this.hasFiles(event)
    if (!hasFiles || !this.enabled) {
      return
    }
    const inside = this.isInside(event)
    if (!inside && !this.allowDocumentDrop) {
      return
    }
    event.preventDefault()
    event.stopPropagation()
    this.queueFiles(Array.from(event.dataTransfer?.files || []))
  }

  onOverlayDragOver(event: DragEvent) {
    event.preventDefault()
    event.stopPropagation()
  }

  onOverlayDragLeave(event: DragEvent) {
    const currentTarget = event.currentTarget as HTMLElement
    if (!currentTarget || !currentTarget.contains(event.relatedTarget as Node)) {
      this.setDragging(false)
    }
  }

  onOverlayDrop(event: DragEvent) {
    event.preventDefault()
    event.stopPropagation()
    this.queueFiles(Array.from(event.dataTransfer?.files || []))
  }

  onUploadComplete(uploadedFileResults: any[]) {
    if (!uploadedFileResults?.length) {
      return
    }
    this.isUploading = false
    this.setDragging(false)
    const attachments = R.map(Attachment.deserialize, uploadedFileResults)
    if (this.dispatchToStore && !this.uploadOptions.cloudinary) {
      this.store.dispatch(addAttachmentsComplete({ attachments }))
    }
    this.uploaded.emit(attachments)
  }

  onUploadError() {
    this.isUploading = false
    this.setDragging(false)
  }

  get uploadOptions(): UploadBoxOptions {
    const base: UploadBoxOptions = {
      ...defaultUploadBoxOptions,
      autoUpload: true,
      maxUploads: 10,
      token: this.authService.token,
    }
    return { ...base, ...this.options }
  }

  private setDragging(state: boolean) {
    if (this.isUploading && !state) {
      this.isUploading = false
    }
    if (this.isDragging === state) {
      return
    }
    this.isDragging = state
    this.dragActiveChange.emit(state)
  }

  get uploadProgress(): number {
    return this.uploadBox?.uploader?.progress || 0
  }

  get showProgress() {
    return this.uploadBox?.uploader?.isUploading
  }

  private hasFiles(event: DragEvent) {
    return Array.from(event.dataTransfer?.types || []).includes('Files')
  }

  private isInside(event: DragEvent) {
    const target = event.target as Node
    if (target && this.elRef.nativeElement.contains(target)) {
      return true
    }
    const hostRect = this.refreshBounds()
    const { clientX, clientY } = event
    return (
      clientX >= hostRect.left && clientX <= hostRect.right && clientY >= hostRect.top && clientY <= hostRect.bottom
    )
  }

  private refreshBounds(): DOMRect {
    return this.elRef.nativeElement.getBoundingClientRect()
  }

  private queueFiles(files: File[]) {
    if (!this.enabled || !files?.length) {
      return
    }
    if (!this.uploadBox?.uploader) {
      return
    }
    this.isUploading = true
    this.setDragging(true)
    this.uploadBox.uploader.addToQueue(files)
    if (!this.uploadOptions.autoUpload) {
      this.uploadBox.upload()
    }
  }
}
